import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// @atlaskit
import { Checkbox } from '@atlaskit/checkbox';
import { colors } from '@atlaskit/theme';
import Button, { ButtonGroup } from '@atlaskit/button';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import DynamicTable from '@atlaskit/dynamic-table';
import EditFilledIcon from '@atlaskit/icon/glyph/edit-filled';

// @source
import { crypto } from '../../../../../utils';
import { Messages } from '../../../../components';
import { User as API } from '../../../../../server';
import DeleteMultipleUsers from './DeleteMultipleUsers';
import OperationButton from './OperationButton';

const initialState = {
  confirm: false,
  message: false,
  isLoading: '',
  multiple: [],
};

const initialStatic = {
  page: 1,
  single: '',
  sortKey: 'creation-time',
  sortOrder: 'DESC',
};

const renderDynamicTableHead = {
  cells: [{
    key: 'checkbox',
    content: '',
    width: 30,
  }, {
    key: 'name',
    content: 'Name',
    isSortable: true,
    width: 110,
  }, {
    key: 'surname',
    content: 'Surname',
    isSortable: true,
    width: 135,
  }, {
    key: 'username',
    content: 'User Name',
    isSortable: true,
    width: 400,
  }, {
    key: 'creation-time',
    content: 'Creation Time',
    isSortable: true,
    width: 200,
  }, {
    key: 'transactions',
    content: '',
    width: 73,
  }],
};

class TableView extends React.Component {
  constructor(props) {
    super(props);
    this.state = initialState;
    this.static = initialStatic;
    this.unmount = false;
    this.onChangeCheckbox = this.onChangeCheckbox.bind(this);
    this.onToggleConfirm = this.onToggleConfirm.bind(this);
  }

  componentDidMount() {
    if (!this.props.users.override) {
      API.fetch({
        dispatch: this.props.dispatch,
        success: () => {
          this.setStateUnmount({ isLoading: '' });
        },
        error: (messages) => {
          this.setStateUnmount({
            isLoading: '',
            message: (
              <Messages
                items={messages}
                onClose={() => this.setState({ message: false })}
              />
            ),
          });
        },
      });
    }
  }

  componentWillUnmount() {
    this.unmount = true;
  }

  onChangeCheckbox(event) {
    const { value, checked } = event.target;
    const multiple = checked
      ? this.addUsersToDelete([value])
      : this.removeUsersToDelete([value]);

    this.setState({ multiple });
  }

  onToggleConfirm(reset = false) {
    const multiple = typeof reset === 'boolean' && reset ? [] : this.state.multiple;

    this.setState({
      confirm: !this.state.confirm,
      multiple,
    });
  }

  setStateUnmount(state) {
    if (this.unmount) return;
    this.setState(state);
  }

  setStatic(state) {
    this.static = { ...this.static, ...state };
  }

  isDeleted(username) {
    return this.state.multiple.includes(username);
  }

  addUsersToDelete(users = []) {
    return [...this.state.multiple, ...users];
  }

  removeUsersToDelete(users = []) {
    const multiple = this.state.multiple.reduce((list, item) => {
      if (!users.includes(item)) {
        list.push(item);
      }
      return list;
    }, []);

    return multiple;
  }

  renderDynamicTableRows() {
    return this.props.users.data.map(user => ({
      key: user.Username,
      style: this.isDeleted(user.Username) ? ({
        backgroundColor: colors.R50,
      }) : null,
      cells: [
        {
          content: (
            <Checkbox
              onChange={this.onChangeCheckbox}
              value={user.Username}
            />
          ),
        },
        { content: user.Name },
        { content: user.Surname },
        { content: user.Username },
        { content: user.CreationDate },
        {
          content: (
            <div className="spotlight">
              <ButtonGroup>
                {OperationButton({
                  href: `edit?uuid=${crypto.encode(user.Username)}`,
                  Icon: EditFilledIcon,
                  tooltip: 'Edit user',
                })}
                {OperationButton({
                  href: `delete?uuid=${crypto.encode(user.Username)}`,
                  Icon: CrossCircleIcon,
                  tooltip: 'Delete user',
                })}
              </ButtonGroup>
            </div>
          ),
        },
      ],
    }));
  }

  renderDeleteUsersButton() {
    return (
      <Button
        appearance="primary"
        isDisabled={this.state.multiple.length < 2}
        onClick={this.onToggleConfirm}
      >
        Delete users
      </Button>
    );
  }

  render() {
    return (
      <div className="dynamic-table" style={{ marginTop: 25 }}>
        <DynamicTable
          defaultSortKey={this.static.sortKey}
          defaultSortOrder={this.static.sortOrder}
          emptyView={this.state.message ? false : <i>No users have been created yet.</i>}
          head={renderDynamicTableHead}
          isFixedSize
          isLoading={!this.props.users.override}
          loadingSpinnerSize="large"
          onSetPage={page => this.setStatic({ page })}
          page={this.static.page}
          rows={this.renderDynamicTableRows()}
          rowsPerPage={11}
          onSort={(value) => {
            this.setStatic({
              sortKey: value.key,
              sortOrder: value.sortOrder,
            });
          }}
        />
        <br />
        {this.renderDeleteUsersButton()}
        {this.state.confirm ? (
          <DeleteMultipleUsers
            onClose={this.onToggleConfirm}
            users={this.state.multiple}
          />
        ) : null}
        {this.state.message}
      </div>
    );
  }
}

TableView.propTypes = {
  dispatch: PropTypes.func.isRequired,
  users: PropTypes.shape().isRequired,
};

const mapStateToProps = state => ({
  users: state.User,
});

export default connect(mapStateToProps)(TableView);
